import { useEffect, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { useAdminOfferSettings, useSaveOfferSettings } from '@/lib/admin/offer-admin';

function Switch({
  checked,
  label,
  disabled,
  onChange,
}: {
  checked: boolean;
  label: string;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="flex h-11 w-12 shrink-0 items-center justify-center disabled:opacity-50"
    >
      <span
        className={cn(
          'relative inline-flex h-6 w-11 rounded-full border-2 border-transparent transition-colors',
          checked ? 'bg-accent-400' : 'bg-ink-200',
        )}
      >
        <span
          className={cn(
            'pointer-events-none inline-block h-5 w-5 rounded-full bg-[#ffffff] shadow transition-transform',
            checked ? 'translate-x-5' : 'translate-x-0',
          )}
        />
      </span>
    </button>
  );
}

function Row({
  title,
  detail,
  children,
}: {
  title: string;
  detail: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-ink-100 flex items-center gap-3 border-t py-3 first:border-t-0">
      <div className="min-w-0 flex-1">
        <p className="text-ink-900 text-sm font-semibold">{title}</p>
        <p className="text-ink-500 text-sm">{detail}</p>
      </div>
      {children}
    </div>
  );
}

/**
 * Abonnements et pass (migration 0717) : Pass 24 h et abonnement 7 jours
 * proposés ou non ; essai gratuit des abonnements mensuels. Enregistré au
 * toucher, appliqué tout de suite (checkouts et écrans).
 */
export function OfferSettingsPanel() {
  const settings = useAdminOfferSettings();
  const save = useSaveOfferSettings();
  const [days, setDays] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings.data) setDays(String(settings.data.trial_days));
  }, [settings.data]);

  if (settings.isLoading) return <div className="bg-ink-100 h-40 animate-pulse rounded-2xl" />;
  if (!settings.data) {
    return <p className="text-error-600 text-sm">Impossible de charger les réglages.</p>;
  }
  const s = settings.data;
  const busy = save.isPending;

  function patch(p: Parameters<typeof save.mutate>[0]) {
    setError(null);
    save.mutate(p, { onError: () => setError('Le réglage n’a pas été enregistré. Réessaie.') });
  }

  function saveDays() {
    const n = Number(days);
    if (!Number.isInteger(n) || n < 1 || n > 30) {
      setError('Durée de l’essai entre 1 et 30 jours.');
      return;
    }
    if (n !== s.trial_days) patch({ trial_days: n });
  }

  return (
    <section
      aria-labelledby="offer-settings-title"
      className="border-ink-200 space-y-2 rounded-2xl border bg-white p-4 sm:p-5"
    >
      <h2 id="offer-settings-title" className="font-display text-ink-900 text-lg font-bold">
        Abonnements et pass
      </h2>
      <p className="text-ink-500 text-sm">
        L’abonnement 30 jours est toujours proposé. Un réglage s’applique tout de suite aux écrans
        d’achat et aux paiements.
      </p>

      <div>
        <Row
          title="Essai gratuit des abonnements mensuels"
          detail={
            s.trial_enabled
              ? `${s.trial_days} jours gratuits sur l’abonnement 30 jours, Élève et Famille, une fois par compte. La carte bancaire est demandée au départ.`
              : 'Le premier mois est payé dès l’inscription.'
          }
        >
          <Switch
            checked={s.trial_enabled}
            label="Essai gratuit des abonnements mensuels"
            disabled={busy}
            onChange={(v) => patch({ trial_enabled: v })}
          />
        </Row>
        {s.trial_enabled && (
          <div className="flex flex-wrap items-end gap-2 pb-3">
            <label className="space-y-1">
              <span className="font-display text-ink-700 block text-xs font-semibold">
                Durée de l’essai (jours)
              </span>
              <input
                className="border-ink-200 text-ink-900 focus:ring-primary-400 min-h-[2.75rem] w-28 rounded-xl border bg-white px-3 text-sm focus:outline-none focus:ring-2"
                inputMode="numeric"
                value={days}
                onChange={(e) => setDays(e.target.value)}
                onBlur={saveDays}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveDays();
                }}
              />
            </label>
          </div>
        )}
        <Row title="Pass 24 h" detail="Paiement unique, sans engagement (1,99 €, épreuves 2,99 €).">
          <Switch
            checked={s.pass_24h_enabled}
            label="Proposer le Pass 24 h"
            disabled={busy}
            onChange={(v) => patch({ pass_24h_enabled: v })}
          />
        </Row>
        <Row
          title="Abonnement 7 jours"
          detail="Renouvelé chaque semaine, résiliable (3,99 €, épreuves 5,99 €)."
        >
          <Switch
            checked={s.pass_7d_enabled}
            label="Proposer l’abonnement 7 jours"
            disabled={busy}
            onChange={(v) => patch({ pass_7d_enabled: v })}
          />
        </Row>
      </div>
      <p className="text-ink-500 text-xs">
        Applications iPhone et Android : l’essai gratuit se règle aussi dans App Store Connect et la
        Play Console (offre de lancement).
      </p>
      {error && (
        <p role="alert" className="text-error-600 text-sm">
          {error}
        </p>
      )}
    </section>
  );
}
